/**
 * usePullToRefresh — 어떤 페이지에도 적용 가능한 당겨서 새로고침 훅.
 *
 * 사용:
 *   const ref = useRef<HTMLDivElement>(null);
 *   const { pullDistance, isRefreshing, isPulling, progress } = usePullToRefresh(ref, {
 *     onRefresh: () => refetch(),
 *   });
 *
 * 동작:
 *   - targetRef 의 nearest scrollable 조상을 탐색해 scrollTop===0 일 때만 발동.
 *   - 첫 8px 이동으로 vertical/horizontal 방향 판정 — 가로 스와이프(SwipeTabs)는 무시.
 *   - 임계값 도달 후 release 하면 onRefresh 실행, Promise 종료까지 잠금 유지.
 *   - touchmove 에서 vertical pull 일 때만 preventDefault — 가로 스와이프는 통과.
 */
import { useEffect, useRef, useState, type RefObject } from "react";

interface UsePullToRefreshOptions {
  /** 임계값을 넘긴 채 손가락을 떼면 호출. Promise 를 반환하면 그동안 잠금. */
  onRefresh: () => void | Promise<unknown>;
  /** 새로고침이 발동하는 pull 거리 (px). default 70 */
  threshold?: number;
  /** 시각적으로 보여줄 최대 pull 거리 (px). default 120 */
  maxDistance?: number;
  /** 저항 계수. 클수록 손가락 이동 대비 컨텐츠가 적게 움직임. default 2.5 */
  resistance?: number;
  /** false 면 listener 등록 안 함. default true */
  enabled?: boolean;
}

interface PullToRefreshState {
  /** 현재 컨텐츠를 아래로 밀어내야 할 거리 (px) */
  pullDistance: number;
  /** onRefresh 가 진행 중인 동안 true */
  isRefreshing: boolean;
  /** 손가락이 닿아있고 vertical pull 중일 때 true */
  isPulling: boolean;
  /** threshold 대비 진행률 (0..1). 시각 효과용. */
  progress: number;
}

const DEFAULT_THRESHOLD = 70;
const DEFAULT_MAX_DISTANCE = 120;
const DEFAULT_RESISTANCE = 2.5;
const DIRECTION_LOCK_PX = 8;
/** onRefresh 가 끝나지 않아도 이 시간이 지나면 강제로 스피너를 닫는다 (멈춤 방지). */
const REFRESH_SAFETY_MS = 10_000;

function findScrollableAncestor(start: HTMLElement): HTMLElement {
  let current: HTMLElement | null = start.parentElement;
  while (current) {
    const style = getComputedStyle(current);
    const overflowY = style.overflowY;
    const isScrollable =
      (overflowY === "auto" || overflowY === "scroll") &&
      current.scrollHeight > current.clientHeight;
    if (isScrollable) return current;
    current = current.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

export function usePullToRefresh(
  targetRef: RefObject<HTMLElement | null>,
  options: UsePullToRefreshOptions,
): PullToRefreshState {
  const {
    onRefresh,
    threshold = DEFAULT_THRESHOLD,
    maxDistance = DEFAULT_MAX_DISTANCE,
    resistance = DEFAULT_RESISTANCE,
    enabled = true,
  } = options;

  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);

  // 핸들러 내부에서 최신값을 읽기 위한 ref. effect 가 매 state 변화마다 재등록되지 않도록.
  const liveRef = useRef({ pullDistance: 0, isRefreshing: false });
  liveRef.current.pullDistance = pullDistance;
  liveRef.current.isRefreshing = isRefreshing;

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return;
    const target = targetRef.current;
    if (!target) return;

    // scrollable 조상은 매 터치마다 새로 탐색한다. 마운트 시점엔 스켈레톤만 떠서
    // 실제 스크롤 컨테이너(.routeMotion 등)가 아직 overflow 하지 않아 탐색에서
    // 누락되고 document 로 폴백되는 일이 잦다 — 그러면 scrollTop 이 늘 0 으로 보여
    // 위로 스크롤하는 제스처를 새로고침으로 오인해 가로채는 문제가 생긴다.
    let scrollContainer: HTMLElement = findScrollableAncestor(target);

    let startY: number | null = null;
    let startX = 0;
    let direction: "vertical" | "horizontal" | null = null;

    const cancelTracking = () => {
      startY = null;
      direction = null;
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (liveRef.current.isRefreshing) return;
      scrollContainer = findScrollableAncestor(target);
      if (scrollContainer.scrollTop > 0) {
        cancelTracking();
        return;
      }
      const touch = event.touches[0];
      if (!touch) return;
      startY = touch.clientY;
      startX = touch.clientX;
      direction = null;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (startY === null) return;
      if (liveRef.current.isRefreshing) return;

      const touch = event.touches[0];
      if (!touch) return;
      const deltaY = touch.clientY - startY;
      const deltaX = touch.clientX - startX;

      if (direction === null) {
        if (Math.abs(deltaX) > DIRECTION_LOCK_PX || Math.abs(deltaY) > DIRECTION_LOCK_PX) {
          direction = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
        }
      }

      // 가로 스와이프는 SwipeTabs 같은 자식이 처리하게 둔다.
      if (direction === "horizontal") return;

      // 스크롤이 위에서 떨어졌거나 위로 끌었으면 추적 종료 + 인디케이터 닫기.
      if (scrollContainer.scrollTop > 0 || deltaY <= 0) {
        if (liveRef.current.pullDistance > 0) {
          setPullDistance(0);
          setIsPulling(false);
        }
        cancelTracking();
        return;
      }

      const damped = Math.min(maxDistance, deltaY / resistance);
      setPullDistance(damped);
      setIsPulling(true);
      if (event.cancelable) event.preventDefault();
    };

    const handleTouchEnd = () => {
      if (startY === null) return;
      const finalPull = liveRef.current.pullDistance;
      cancelTracking();

      if (finalPull >= threshold && !liveRef.current.isRefreshing) {
        setIsRefreshing(true);
        setPullDistance(threshold * 0.75); // 잠금 위치 (loader 가 보이는 높이)
        setIsPulling(false);

        // onRefresh 가 영영 settle 되지 않아도(예: 비활성 쿼리 refetch 가 매달림,
        // 네트워크 지연) 스피너가 무한정 돌지 않도록 settle/타임아웃 중 먼저 오는 쪽에서 닫는다.
        let settled = false;
        const reset = () => {
          if (settled) return;
          settled = true;
          setIsRefreshing(false);
          setPullDistance(0);
        };
        const safetyTimer = window.setTimeout(reset, REFRESH_SAFETY_MS);
        Promise.resolve()
          .then(() => onRefreshRef.current())
          .catch(() => {
            /* swallow — UI 는 어쨌든 초기화 */
          })
          .finally(() => {
            window.clearTimeout(safetyTimer);
            reset();
          });
      } else {
        setPullDistance(0);
        setIsPulling(false);
      }
    };

    target.addEventListener("touchstart", handleTouchStart, { passive: true });
    // touchmove 는 preventDefault 가 필요하므로 passive: false.
    target.addEventListener("touchmove", handleTouchMove, { passive: false });
    target.addEventListener("touchend", handleTouchEnd, { passive: true });
    target.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      target.removeEventListener("touchstart", handleTouchStart);
      target.removeEventListener("touchmove", handleTouchMove);
      target.removeEventListener("touchend", handleTouchEnd);
      target.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [enabled, threshold, maxDistance, resistance, targetRef]);

  const progress = threshold > 0 ? Math.min(1, pullDistance / threshold) : 0;

  return {
    pullDistance,
    isRefreshing,
    isPulling,
    progress,
  };
}
